import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import api from "../../../../services/api";

const ReviewSection = ({ productId }) => {
  // 🔥 form state
  const [formData, setFormData] = useState({
    name: "",
    rating: "",
    comment: "",
  });

  // 🔥 review list state
  const [reviews, setReviews] = useState([]);

  // 🔥 loading states
  const [loading, setLoading] = useState(false);
  const [reviewLoading, setReviewLoading] =
    useState(true);

  // 🔥 alert messages
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ⭐ render stars
  const renderStars = (rating) => {
    let stars = "";

    for (let i = 1; i <= 5; i++) {
      stars += i <= rating ? "★" : "☆";
    }

    return stars;
  };

  // 🔥 handle input change
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // 🔥 fetch reviews
  const fetchReviews = useCallback(async () => {
    try {
      setReviewLoading(true);

      const response = await api.get(
        `/product-reviews/${productId}`
      );

      setReviews(response.data.data || []);
    } catch (error) {
      console.error(
        "Review fetch error:",
        error
      );
    } finally {
      setReviewLoading(false);
    }
  }, [productId]);

  // 🔥 load reviews on page load
  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId, fetchReviews]);

  // 🔥 submit review
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const payload = {
        product_id: productId,
        name: formData.name,
        rating: formData.rating,
        review: formData.comment,
      };

      const response = await api.post(
        "/store-product-reviews",
        payload
      );

      if (response.status === 200) {
        setSuccessMsg(
          response.data.message
        );

        // 🔥 reset form
        setFormData({
          name: "",
          rating: "",
          comment: "",
        });

        // 🔥 reload reviews
        fetchReviews();
      }
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message ||
          "Something went wrong!"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">

      {/* LEFT SIDE - REVIEW LIST */}
      <div className="col-md-7 order-2 order-md-1">
        <h5 className="mb-3">
          Customer Reviews
        </h5>

        {reviewLoading ? (
          <div className="text-center py-4">
            Loading reviews...
          </div>
        ) : reviews.length > 0 ? (
          <>
            {reviews.map((review) => (
              <div
                className="card mb-3 shadow-sm"
                key={review.id}
              >
                <div className="card-body">

                  {/* name + time */}
                  <div className="d-flex justify-content-between">
                    <h6 className="mb-1">
                      {review.name}
                    </h6>

                    <small className="text-muted">
                      {review.time_ago}
                    </small>
                  </div>

                  {/* rating */}
                  <div className="text-warning mb-2">
                    {renderStars(
                      review.rating
                    )}
                  </div>

                  {/* review */}
                  <p className="mb-0">
                    {review.review}
                  </p>

                </div>
              </div>
            ))}

            <div className="text-muted">
              No more reviews...
            </div>
          </>
        ) : (
          <div className="alert alert-secondary">
            No reviews found.
          </div>
        )}
      </div>

      {/* RIGHT SIDE - REVIEW FORM */}
      <div className="col-md-5 order-1 order-md-2">
        <h5 className="mb-3">
          Write a Review
        </h5>

        <div className="card shadow-sm">
          <div className="card-body">

            {/* success message */}
            {successMsg && (
              <div className="alert alert-success">
                {successMsg}
              </div>
            )}

            {/* error message */}
            {errorMsg && (
              <div className="alert alert-danger">
                {errorMsg}
              </div>
            )}

            <form onSubmit={handleSubmit}>

              {/* name */}
              <div className="mb-3">
                <label className="form-label">
                  Your Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
              </div>

              {/* rating */}
              <div className="mb-3">
                <label className="form-label">
                  Rating
                </label>

                <select
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="">
                    Select Rating
                  </option>

                  <option value="5">
                    ⭐⭐⭐⭐⭐ (5)
                  </option>

                  <option value="4">
                    ⭐⭐⭐⭐ (4)
                  </option>

                  <option value="3">
                    ⭐⭐⭐ (3)
                  </option>

                  <option value="2">
                    ⭐⭐ (2)
                  </option>

                  <option value="1">
                    ⭐ (1)
                  </option>
                </select>
              </div>

              {/* comment */}
              <div className="mb-3">
                <label className="form-label">
                  Your Review
                </label>

                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  className="form-control"
                  rows="4"
                  required
                ></textarea>
              </div>

              {/* submit button */}
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading
                  ? "Submitting..."
                  : "Submit Review"}
              </button>

            </form>

          </div>
        </div>
      </div>

    </div>
  );
};

export default ReviewSection;